// frontend/src/pages/ConversationalMode.jsx - Complete updated version with page unload cleanup
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
        MessageCircle,
        Globe,
        Play,
        Square,
        Settings,
        Monitor,
        Maximize2
} from 'lucide-react'
import { useWebSocket } from '../hooks/useWebSocket'
import { useBrowserSession } from '../hooks/useBrowserSession'
import { useAuth } from '../hooks/useAuth'
import ChatInterface from '../components/chat/ChatInterface'
import VideoStream from '../components/video/VideoStream'

// Use react-browser-ui for a fully glassy browser frame

// Custom Glassy Browser UI (no react-browser-ui)
const BrowserUI = ({ url, children, onReload, onBack, onForward, onClose, isLoading }) => (
  <div className="relative rounded-3xl shadow-2xl overflow-hidden border border-white/30 bg-white/20 backdrop-blur-2xl max-w-6xl w-full mx-auto" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)' }}>
    {/* Browser Bar */}
    <div className="flex items-center px-6 py-3 bg-white/30 backdrop-blur-xl border-b border-white/20" style={{ minHeight: 56 }}>
      {/* Traffic lights */}
      <div className="flex space-x-2 mr-4">
        <span className="w-3 h-3 rounded-full bg-red-400 border border-red-300 shadow cursor-pointer" onClick={onClose}></span>
        <span className="w-3 h-3 rounded-full bg-yellow-300 border border-yellow-200 shadow cursor-pointer" onClick={onBack}></span>
        <span className="w-3 h-3 rounded-full bg-green-400 border border-green-300 shadow cursor-pointer" onClick={onForward}></span>
      </div>
      {/* Address bar */}
      <div className="flex-1 mx-4">
        <input
          type="text"
          value={url}
          readOnly
          className="w-full px-4 py-2 rounded-xl bg-white/60 text-gray-800 text-sm border border-white/30 focus:outline-none cursor-default font-mono shadow-inner"
          style={{ letterSpacing: '0.01em', fontWeight: 500 }}
        />
      </div>
      {/* Reload */}
      <button onClick={onReload} className="ml-3 text-gray-500 hover:text-primary-600 transition-colors" title="Reload">
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4v5h.582M20 20v-5h-.581M5.07 19A9 9 0 1 0 12 3v1"/></svg>
      </button>
      {isLoading && <span className="ml-3 animate-spin w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full" />}
    </div>
    {/* Content (video stream) */}
    <div className="bg-white/10 backdrop-blur-2xl p-2" style={{ minHeight: 340 }}>
      {children}
    </div>
  </div>
)
import toast from 'react-hot-toast'
import WebSocketService from '../services/websocket'

const ConversationalMode = () => {
        const { user } = useAuth()
        const { sendMessage, lastMessage, connectionStatus } = useWebSocket()
        const { createSession, activeSession, updateSession } = useBrowserSession()

        // Core state
        const [messages, setMessages] = useState([])
        const [isTyping, setIsTyping] = useState(false)
        const [currentUrl, setCurrentUrl] = useState('')
        const [sessionActive, setSessionActive] = useState(false)
        const [browserStatus, setBrowserStatus] = useState('idle')
        const [isFullscreen, setIsFullscreen] = useState(false)
        const [videoStreaming, setVideoStreaming] = useState(false)
        const [directConnectionStatus, setDirectConnectionStatus] = useState('disconnected')
        const [videoFrames, setVideoFrames] = useState([])

        // Enhanced state for better cleanup handling
        const [isStopping, setIsStopping] = useState(false)
        const [isUnloading, setIsUnloading] = useState(false)

        // Refs to track current state for cleanup
        const sessionActiveRef = useRef(false)
        const isStoppingRef = useRef(false)
        const activeSessionRef = useRef(null)
        const cleanupTriggeredRef = useRef(false)

        // Update refs whenever state changes
        useEffect(() => {
                sessionActiveRef.current = sessionActive
                isStoppingRef.current = isStopping
                activeSessionRef.current = activeSession
        }, [sessionActive, isStopping, activeSession])

        // **ENHANCED: Direct WebSocket connection with proper session handling**
        useEffect(() => {
                console.log('🔧 Setting up WebSocket connection...')

                const sessionId = user?.id || 'default-session'
                WebSocketService.connect(sessionId)

                // Listen for messages directly
                WebSocketService.addListener('message', (message) => {
                        console.log('📨 Direct WebSocket message:', message.type, message)
                        handleWebSocketMessage(message)
                })

                WebSocketService.addListener('open', () => {
                        setDirectConnectionStatus('connected')
                        console.log('✅ Direct WebSocket connected')

                        // Check for orphaned sessions when connected
                        checkForOrphanedSessions()
                })

                WebSocketService.addListener('close', () => {
                        setDirectConnectionStatus('disconnected')
                        console.log('❌ Direct WebSocket disconnected')
                })

                WebSocketService.addListener('error', (error) => {
                        setDirectConnectionStatus('error')
                        console.error('❌ Direct WebSocket error:', error)
                })

                return () => {
                        performCleanup('websocket_cleanup')
                        WebSocketService.disconnect()
                }
        }, [user?.id])

        // CRITICAL: Cleanup function that can be called from anywhere
        const performCleanup = useCallback(async (reason = 'unknown') => {
                if (cleanupTriggeredRef.current) {
                        console.log('🔄 Cleanup already triggered, skipping...')
                        return
                }

                cleanupTriggeredRef.current = true
                console.log(`🧹 Performing cleanup due to: ${reason}`)

                try {
                        if (sessionActiveRef.current && !isStoppingRef.current) {
                                console.log('🛑 Active session detected, sending stop message...')

                                if (WebSocketService.isConnected()) {
                                        const stopMessage = {
                                                type: 'stop_conversation',
                                                data: {
                                                        session_id: activeSessionRef.current?.id,
                                                        reason: reason,
                                                        forced_cleanup: true,
                                                        timestamp: new Date().toISOString()
                                                }
                                        }

                                        console.log('📤 Sending cleanup stop message:', stopMessage)
                                        WebSocketService.send(stopMessage)

                                        // Give a moment for the message to be sent
                                        await new Promise(resolve => setTimeout(resolve, 100))
                                } else {
                                        console.log('⚠️ WebSocket not connected, cannot send stop message')
                                }
                        }

                        if (WebSocketService.isConnected()) {
                                console.log('🔌 Disconnecting WebSocket...')
                                WebSocketService.disconnect()
                        }

                        console.log(`✅ Cleanup completed for reason: ${reason}`)

                } catch (error) {
                        console.error('❌ Error during cleanup:', error)
                }
        }, [])

        // Check for orphaned sessions on connection
        const checkForOrphanedSessions = useCallback(() => {
                if (user?.id) {
                        console.log('🔍 Checking for orphaned sessions...')

                        WebSocketService.send({
                                type: 'cleanup_orphaned_sessions',
                                data: {
                                        user_id: user.id,
                                        timestamp: new Date().toISOString()
                                }
                        })
                }
        }, [user?.id])

        // **PAGE UNLOAD HANDLERS**

        // beforeunload event listener (most important for page refresh/close)
        useEffect(() => {
                const handleBeforeUnload = (event) => {
                        console.log('⚠️ Page about to unload, triggering cleanup...')
                        setIsUnloading(true)

                        if (sessionActiveRef.current && !isStoppingRef.current) {
                                // Use sendBeacon for reliable delivery during page unload
                                if (navigator.sendBeacon && WebSocketService.isConnected()) {
                                        const stopMessage = JSON.stringify({
                                                type: 'stop_conversation',
                                                data: {
                                                        session_id: activeSessionRef.current?.id,
                                                        reason: 'page_unload',
                                                        forced_cleanup: true,
                                                        timestamp: new Date().toISOString()
                                                }
                                        })

                                        const wsUrl = WebSocketService.getEndpointUrl()?.replace('ws://', 'http://').replace('wss://', 'https://') + '/cleanup'
                                        navigator.sendBeacon(wsUrl, stopMessage)
                                        console.log('📡 Sent cleanup via sendBeacon')
                                }

                                performCleanup('page_unload')

                                const message = 'Your video browsing session is still active. Are you sure you want to leave?'
                                event.returnValue = message
                                return message
                        }
                }

                window.addEventListener('beforeunload', handleBeforeUnload)
                return () => window.removeEventListener('beforeunload', handleBeforeUnload)
        }, [performCleanup])

        // pagehide event (for mobile browsers)
        useEffect(() => {
                const handlePageHide = () => {
                        console.log('👋 Page hiding, triggering cleanup...')
                        performCleanup('page_hide')
                }

                window.addEventListener('pagehide', handlePageHide)
                return () => window.removeEventListener('pagehide', handlePageHide)
        }, [performCleanup])

        // Handle browser back/forward buttons
        useEffect(() => {
                const handlePopState = () => {
                        console.log('↩️ Browser navigation detected')
                        if (sessionActiveRef.current) {
                                performCleanup('browser_navigation')
                        }
                }

                window.addEventListener('popstate', handlePopState)
                return () => window.removeEventListener('popstate', handlePopState)
        }, [performCleanup])

        // Component unmount cleanup
        useEffect(() => {
                return () => {
                        console.log('🧹 Component unmounting...')
                        performCleanup('component_unmount')
                }
        }, [performCleanup])

        // Use direct connection status
        const effectiveConnectionStatus = directConnectionStatus

        const handleWebSocketMessage = (messageData) => {
                console.log('🔄 Processing message:', messageData.type, messageData)

                switch (messageData.type) {
                        case 'conversation_response':
                                setIsTyping(false)
                                addMessage('assistant', messageData.data.message, {
                                        timestamp: messageData.data.timestamp,
                                        actions: messageData.data.actions,
                                        type: 'text',
                                        video_streaming: messageData.data.video_streaming
                                })
                                break

                        case 'browser_action':
                                addMessage('system', messageData.data.message || `Browser action: ${messageData.data.action}`, {
                                        action: messageData.data.action,
                                        url: messageData.data.url,
                                        imageUrl: messageData.data.screenshot,
                                        type: messageData.data.video_streaming ? 'action' : (messageData.data.screenshot ? 'image' : 'action'),
                                        video_streaming: messageData.data.video_streaming
                                })
                                if (messageData.data.url) {
                                        setCurrentUrl(messageData.data.url)
                                }
                                if (messageData.data.video_streaming) {
                                        setVideoStreaming(true)
                                }
                                break

                        case 'video_frame':
                                console.log('🎥 Processing video frame:', {
                                        hasFrame: !!messageData.data?.frame,
                                        timestamp: messageData.data?.timestamp,
                                        frameSize: messageData.data?.frame?.length || 0
                                })

                                if (messageData.data?.frame) {
                                        const frameData = {
                                                id: Date.now() + Math.random(),
                                                frame: messageData.data.frame,
                                                timestamp: messageData.data.timestamp || new Date().toISOString()
                                        }

                                        setVideoFrames(prev => {
                                                const newFrames = [...prev, frameData]
                                                return newFrames.slice(-10) // Keep only last 10 frames
                                        })

                                        setVideoStreaming(true)
                                        setBrowserStatus('ready')

                                        console.log('✅ Video frame stored, total frames:', videoFrames.length + 1)
                                }
                                break

                        case 'browser_status':
                                setBrowserStatus(messageData.data.status)

                                if (messageData.data.status === 'ready' && !isStopping) {
                                        // toast.success('Browser is ready!')
                                } else if (messageData.data.status === 'stopped') {
                                        console.log('🔴 Browser status confirmed as stopped')
                                        setIsStopping(false)
                                        setSessionActive(false)
                                        setVideoStreaming(false)
                                        setVideoFrames([])
                                        cleanupTriggeredRef.current = false // Reset for next session
                                } else if (messageData.data.status === 'error') {
                                        console.error('🔴 Browser status error')
                                        setIsStopping(false)
                                        toast.error('Browser error occurred')
                                }
                                break

                        case 'conversation_stopped':
                                console.log('✅ Received conversation_stopped confirmation')

                                setSessionActive(false)
                                setVideoStreaming(false)
                                setBrowserStatus('stopped')
                                setVideoFrames([])
                                setIsStopping(false)
                                cleanupTriggeredRef.current = false // Reset for next session

                                const stopMessage = messageData.data?.message || 'Video browsing session stopped'
                                if (!isUnloading) {
                                        // toast.success(stopMessage)
                                }

                                addMessage('system', stopMessage, {
                                        type: 'action',
                                        action: 'session_stopped',
                                        timestamp: messageData.data?.timestamp
                                })

                                console.log('🎉 Stop process completed successfully')
                                break

                        case 'error':
                                setIsTyping(false)
                                setIsStopping(false)

                                const errorMessage = messageData.data?.message || 'Unknown error occurred'
                                if (!isUnloading) {
                                        toast.error(errorMessage)
                                }
                                addMessage('system', errorMessage, { type: 'error' })

                                if (errorMessage.toLowerCase().includes('stop')) {
                                        setSessionActive(false)
                                        setVideoStreaming(false)
                                        setBrowserStatus('error')
                                }
                                break

                        default:
                                console.log('❓ Unknown message type:', messageData.type)
                }
        }

        const addMessage = (role, content, metadata = {}) => {
                if (metadata.type === 'video_frame') {
                        return
                }

                const newMessage = {
                        id: Date.now() + Math.random(),
                        role,
                        content,
                        timestamp: new Date().toISOString(),
                        metadata: metadata
                }

                console.log('➕ Adding chat message:', {
                        role,
                        content: content.substring(0, 50) + '...',
                        type: metadata.type,
                        messageId: newMessage.id
                })

                setMessages(prev => [...prev, newMessage])

                if (activeSession) {
                        updateSession(activeSession.id, {
                                metadata: {
                                        ...activeSession.metadata,
                                        messages: [...(activeSession.metadata.messages || []), newMessage]
                                }
                        })
                }
        }

        const startConversation = async () => {
                try {
                        const session = await createSession('conversational', 'Video Chat Session', {
                                url: currentUrl,
                                video_streaming: true
                        })

                        setSessionActive(true)
                        setBrowserStatus('loading')
                        cleanupTriggeredRef.current = false // Reset cleanup flag

                        const messagePayload = {
                                type: 'start_conversation',
                                data: {
                                        session_id: session.id,
                                        url: currentUrl || 'https://google.com',
                                        video_streaming: true
                                }
                        }

                        WebSocketService.send(messagePayload)

                        addMessage('system', 'Starting video browsing session...', {
                                action: 'session_start',
                                type: 'action'
                        })

                } catch (error) {
                        toast.error('Failed to start conversation')
                        console.error('Start conversation error:', error)
                }
        }

        const handleSendMessage = async (message) => {
                if (!message.trim()) return

                const userMessage = message.trim()
                setIsTyping(true)

                addMessage('user', userMessage, { type: 'text' })

                try {
                        if (!sessionActive) {
                                await startConversation()
                                await new Promise(resolve => setTimeout(resolve, 1000))
                        }

                        const messagePayload = {
                                type: 'conversation_message',
                                data: {
                                        message: userMessage,
                                        session_id: activeSession?.id,
                                        current_url: currentUrl
                                }
                        }

                        WebSocketService.send(messagePayload)

                } catch (error) {
                        setIsTyping(false)
                        toast.error('Failed to send message')
                        console.error('Send message error:', error)
                }
        }

        const stopConversation = useCallback(async (reason = 'user_action') => {
                if (isStoppingRef.current || isUnloading) {
                        console.log('🔄 Stop already in progress or page unloading, ignoring...')
                        return
                }

                console.log(`🛑 Stop conversation triggered by: ${reason}`)
                setIsStopping(true)
                isStoppingRef.current = true

                try {
                        setSessionActive(false)
                        setVideoStreaming(false)
                        setBrowserStatus('stopping')
                        sessionActiveRef.current = false

                        const stopMessage = {
                                type: 'stop_conversation',
                                data: {
                                        session_id: activeSessionRef.current?.id,
                                        reason: reason,
                                        timestamp: new Date().toISOString()
                                }
                        }

                        console.log('📤 Sending stop message:', stopMessage)
                        WebSocketService.send(stopMessage)

                        setVideoFrames([])
                        setCurrentUrl('')

                        // FIX: Update session status to cancelled (lowercase)
                        if (activeSession) {
                                updateSession(activeSession.id, {
                                        status: 'cancelled', // Use lowercase status
                                        metadata: {
                                                ...activeSession.metadata,
                                                stopped_at: new Date().toISOString(),
                                                stop_reason: reason
                                        }
                                });
                        }

                        if (!isUnloading) {
                                addMessage('system', `Stopping video browsing session... (${reason})`, {
                                        type: 'action',
                                        action: 'stopping',
                                        reason: reason
                                })

                                toast.success('Stopping session...')
                        }

                        console.log('✅ Stop process initiated successfully')

                } catch (error) {
                        console.error('❌ Error during stop process:', error)
                        if (!isUnloading) {
                                toast.error('Error stopping session')
                                setIsStopping(false)
                                isStoppingRef.current = false
                                setBrowserStatus('error')
                        }
                }
        }, [isUnloading, activeSession, updateSession])

        const clearChat = () => {
                setMessages([])
                setVideoFrames([])
                if (activeSession) {
                        updateSession(activeSession.id, {
                                metadata: {
                                        ...activeSession.metadata,
                                        messages: []
                                }
                        })
                }
        }

        const toggleFullscreen = () => {
                setIsFullscreen(!isFullscreen)
        }

        // Enhanced Status Components
        const SessionStatusIndicator = () => (
                <div className="flex items-center space-x-2">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${effectiveConnectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
                                        effectiveConnectionStatus === 'connecting' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                }`}>
                                {effectiveConnectionStatus}
                        </div>

                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${browserStatus === 'ready' ? 'bg-green-100 text-green-800' :
                                        browserStatus === 'loading' || browserStatus === 'stopping' ? 'bg-yellow-100 text-yellow-800' :
                                                browserStatus === 'error' ? 'bg-red-100 text-red-800' :
                                                        browserStatus === 'stopped' ? 'bg-gray-100 text-gray-800' :
                                                                'bg-gray-100 text-gray-800'
                                }`}>
                                Browser: {browserStatus}
                                {isStopping && <span className="ml-1">⏳</span>}
                        </div>

                        {videoStreaming && !isStopping && (
                                <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        🔴 Live Video ({videoFrames.length})
                                </div>
                        )}

                        {isStopping && (
                                <div className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        🛑 Stopping...
                                </div>
                        )}
                </div>
        )

        const StopButton = () => (
                <button
                        onClick={() => stopConversation('user_action')}
                        disabled={!sessionActive || isStopping}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm font-medium transition-all ${isStopping
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-red-100 hover:bg-red-200 text-red-700 hover:shadow-md'
                                }`}
                        title={isStopping ? 'Stopping...' : 'Stop video session'}
                >
                        {isStopping ? (
                                <>
                                        <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                        <span>Stopping...</span>
                                </>
                        ) : (
                                <>
                                        <Square size={14} />
                                        <span>Stop</span>
                                </>
                        )}
                </button>
        )

        console.log('📊 Current stats:', {
                totalMessages: messages.length,
                videoFrameCount: videoFrames.length,
                videoStreaming,
                connectionStatus: effectiveConnectionStatus,
                browserStatus,
                latestFrame: videoFrames.length > 0 ? 'present' : 'none'
        })

        return (
            <div className="min-h-screen flex flex-col bg-gray-50 overflow-hidden">
                {/* Header removed for adaptive layout */}

                        {/* URL Input (when not active) - Fixed */}
                        {!sessionActive && (
                                <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3">
                                        <div className="flex items-center space-x-3">
                                                <Globe className="h-5 w-5 text-gray-400" />
                                                <input
                                                        type="url"
                                                        placeholder="Enter website URL to start video browsing (optional)"
                                                        value={currentUrl}
                                                        onChange={(e) => setCurrentUrl(e.target.value)}
                                                        onKeyPress={(e) => {
                                                                if (e.key === 'Enter' && currentUrl.trim()) {
                                                                        startConversation()
                                                                }
                                                        }}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                                {currentUrl.trim() && (
                                                        <button
                                                                onClick={startConversation}
                                                                disabled={effectiveConnectionStatus !== 'connected'}
                                                                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium"
                                                        >
                                                                Start Video
                                                        </button>
                                                )}
                                        </div>
                                </div>
                        )}

                        {/* Main Content - Responsive and adaptive layout */}
                        <div className="flex-1 flex flex-col md:flex-row min-h-0">
                          {/* Video Stream Section */}
                          {(videoStreaming || sessionActive) && (
                            <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black' : 'w-full md:flex-1'} flex items-center justify-center p-2 md:p-6`}>
                              <BrowserUI
                                url={currentUrl || 'https://google.com'}
                                onReload={() => setCurrentUrl(currentUrl)}
                                onBack={() => {}}
                                onForward={() => {}}
                                onClose={() => stopConversation('browser_ui_close')}
                                isLoading={browserStatus === 'loading'}
                              >
                                <div className="relative">
                                  <VideoStream
                                    videoFrames={videoFrames}
                                    connectionStatus={effectiveConnectionStatus}
                                    onFullscreen={toggleFullscreen}
                                  />
                                  {/* Fullscreen Exit Button */}
                                  {isFullscreen && (
                                    <button
                                      onClick={toggleFullscreen}
                                      className="absolute top-4 right-4 bg-gray-900 bg-opacity-80 text-white p-2 rounded-lg hover:bg-opacity-100"
                                    >
                                      <Maximize2 size={20} />
                                    </button>
                                  )}
                                </div>
                              </BrowserUI>
                            </div>
                          )}

                          {/* Chat Interface Section - Responsive width and scrolling */}
                          <div className={`${isFullscreen ? 'hidden' : videoStreaming ? 'w-full md:w-96 flex-shrink-0' : 'w-full'} border-l border-white/20 min-h-0 bg-white/40 backdrop-blur-xl p-0 flex flex-col`} style={{borderTopRightRadius: '1.5rem', borderBottomRightRadius: '1.5rem', boxShadow: '0 4px 24px 0 rgba(31,38,135,0.08)'}}>
                            <div className="h-full p-2 md:p-6 flex-1 flex flex-col">
                              <div className="flex-1 flex flex-col rounded-2xl bg-white/60 backdrop-blur-lg shadow-inner p-2">
                                <ChatInterface
                                  messages={messages}
                                  onSendMessage={handleSendMessage}
                                  isTyping={isTyping}
                                  placeholder={
                                    sessionActive
                                      ? "Tell me what to do on the website..."
                                      : "Type a message to start video browsing..."
                                  }
                                  disabled={effectiveConnectionStatus !== 'connected'}
                                  sessionActive={sessionActive}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                {/* No footer, all glassy look handled by BrowserUI */}
            </div>
        )
}

export default ConversationalMode
